import { Badge } from "react-bootstrap";
import { FaDollarSign, FaPencilAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import Paginate from "../Paginate/Paginate";
import React from "react";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
interface Deal {
  dealNo?: string;
  firstName?: string;
  LastName?: string;
  owner?: string;
  dealSource?: string;
  dealPartner?: string;
  dealCustomer?: string;
  dealstatus?: string;
  dealStage?: string;
  opportunityReceivedDate?: string;
  expectedClosureDate?: string;
  dealTitle?: string;
  winProbability?: string;
  dealTCV?: number;
  dealCurrency?: string;
  dealType?: string;
  dealDurationMonths?: number;
  lastUpdatedOn?: string;
  lastUpdateRemarks?: string;
}

interface TableProps {
  data: Deal[];
  totalPages: number;
  currentPage: number;
  handlePageChange: (page: number) => void;
}

const getStatusVariant = (status: string) => {
  switch (status?.toLowerCase()) {
    case "in-progress":
      return "warning text-dark";
    case "active":
      return "success";
    default:
      return "secondary";
  }
};

const getStageVariant = (stage: string) => {
  switch (stage?.toLowerCase()) {
    case "l0":
      return "warning text-dark";
    case "l1":
      return "primary";
    case "l2":
      return "secondary";
    case "l3":
      return "success";
    default:
      return "dark";
  }
};

const TableComponent: React.FC<TableProps> = ({
  data = [],
  totalPages,
  currentPage,
  handlePageChange,
}) => {
  const navigate = useNavigate();

  const handleEdit = (dealNo?: string) => {
    if (dealNo) navigate(`/edit/${dealNo}`);
  };

  return (
    <div className="relative w-full overflow-x-auto mt-4 max-w-screen-2xl h-full">
      <div className="overflow-auto">
        <table className="text-center align-middle min-w-full border-collapse border-spacing-0">
          <thead>
            <tr>
              {[
                "Deal No",
                "Owner",
                "Source",
                "Partner",
                "Customer",
                "Status",
                "Stage",
                "Title",
                "Total Contract Value",
              ].map((header, index) => (
                <th
                  key={index}
                  className="p-4 whitespace-nowrap text-lg min-w-[150px] text-gray-900 border-gray-300 border-b"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.length > 0 ? (
              data.map((item, index) => (
                <tr
                  key={item.dealNo || index}
                  className={`group ${
                    index % 2 === 0 ? "bg-gray-100" : "bg-white"
                  } border-gray-300 border-b relative`}
                >
                  <td
                    className="relative p-4 border-b border-gray-300 font-semibold text-gray-600 cursor-pointer"
                    onClick={() => handleEdit(item.dealNo)}
                  >
                    {item?.dealNo ?? "N/A"}
                    {item?.dealNo && (
                      <div
                        className="absolute top-1/2 -translate-y-1/2 right-5 hidden group-hover:flex items-center"
                        data-tooltip-id={`edit-tooltip-${index}`}
                        data-tooltip-content="Edit"                      >
                        <>
                          <FaPencilAlt
                            className="text-blue-600 text-lg"
                          />
                          <Tooltip id={`edit-tooltip-${index}`} place="right" className="blue-tooltip"/>
                        </>
                      </div>
                    )}
                  </td>

                  <td
                    className="p-2 whitespace-normal break-words w-32 min-w-[200px] border-b border-gray-300 font-semibold text-gray-600 cursor-pointer"
                    onClick={() => handleEdit(item.dealNo)}
                  >
                    {item?.firstName
                      ? `${item.firstName ?? ""} ${item.LastName}`
                      : "N/A"}
                  </td>

                  <td
                    className="border-b border-gray-300 font-semibold text-gray-600 cursor-pointer"
                    onClick={() => handleEdit(item.dealNo)}
                  >
                    {item?.dealSource ?? "N/A"}
                  </td>

                  <td
                    className="border-b border-gray-300 font-semibold text-gray-600 cursor-pointer"
                    onClick={() => handleEdit(item.dealNo)}
                  >
                    {item?.dealPartner ?? "N/A"}
                  </td>

                  <td
                    className="border-b border-gray-300 font-semibold text-gray-600 cursor-pointer"
                    onClick={() => handleEdit(item.dealNo)}
                  >
                    {item?.dealCustomer ?? "N/A"}
                  </td>

                  <td
                    className="border-b border-gray-300 font-semibold text-gray-600 cursor-pointer"
                    onClick={() => handleEdit(item.dealNo)}
                  >
                    {item?.dealstatus ? (
                      <Badge
                        pill
                        bg={getStatusVariant(item.dealstatus)}
                        className="p-2"
                      >
                        {item.dealstatus}
                      </Badge>
                    ) : (
                      "N/A"
                    )}
                  </td>

                  <td
                    className="border-b border-gray-300 font-semibold text-gray-600 cursor-pointer"
                    onClick={() => handleEdit(item.dealNo)}
                  >
                    {item?.dealStage ? (
                      <Badge pill bg={getStageVariant(item.dealStage)}>
                        {item.dealStage}
                      </Badge>
                    ) : (
                      "N/A"
                    )}
                  </td>

                  <td
                    className="p-2 whitespace-normal break-words w-32 min-w-[200px] border-b border-gray-300 font-semibold text-gray-600 cursor-pointer"
                    onClick={() => handleEdit(item.dealNo)}
                  >
                    {item?.dealTitle ?? "N/A"}
                  </td>

                  <td
                    className="border-b border-gray-300 font-semibold text-gray-600 cursor-pointer"
                    onClick={() => handleEdit(item.dealNo)}
                  >
                    {item?.dealTCV ? (
                      <Badge
                        bg="dark"
                        text="light"
                        className="px-3 py-2 d-inline-flex align-items-center gap-1 text-sm"
                      >
                        <span>{item.dealCurrency ?? ""}</span>
                        <FaDollarSign className="text-green-600" />
                        <span>{item.dealTCV}</span>
                      </Badge>
                    ) : (
                      "N/A"
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={10}
                  className="text-center p-4 text-gray-500 border-b border-gray-300"
                >
                  No data found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Paginate
        currentPage={currentPage}
        totalPages={totalPages > 0 ? totalPages : 1}
        handlePageChange={handlePageChange}
      />
    </div>
  );
};

export default TableComponent;
